/**
 * Action History Tab Component
 * 
 * Displays action history for a specific project
 */

import { useState, useEffect } from 'react'
import {
  Table,
  Space,
  Tag,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Modal,
  Form,
  Input,
  Tooltip,
  Spin,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  ReloadOutlined,
  ToolOutlined,
  HistoryOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/ru'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.locale('ru')

const { Option } = Select
const { TextArea } = Input

interface ActionHistoryTabProps {
  projectId: number
}

interface HistoryItem {
  id: number
  source_id: number
  source: string
  source_type: string
  operator_id: number
  operator_username: string
  operator_full_name: string | null
  old_status: string | null
  new_status: string
  rejected_reason: string | null
  comment: string | null
  created_at: string
}

interface OperatorStats {
  operator_id: number
  operator_username: string
  operator_full_name: string | null
  total_actions: number
  approved_count: number
  rejected_count: number
  reverted_count: number
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидает',
  pre_approved: 'Предодобрен',
  active: 'Одобрен',
  inactive: 'Отклонён',
  deleted: 'Удалён',
}

function ActionHistoryTab({ projectId }: ActionHistoryTabProps) {
  const { operator } = useAuthStore()
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<OperatorStats | null>(null)
  const [allStats, setAllStats] = useState<OperatorStats[]>([])
  
  const isManagerOrAdmin = operator?.role === 'manager' || operator?.role === 'admin'
  
  // * Admin modal state for status override
  const [adminModalVisible, setAdminModalVisible] = useState(false)
  const [adminModalSource, setAdminModalSource] = useState<{
    source_id: number
    source: string
    current_status: string
  } | null>(null)
  const [adminStatusForm] = Form.useForm()
  const [updatingAdminStatus, setUpdatingAdminStatus] = useState(false)
  
  
  // * Always show operator column in project-specific history (multiple operators may work on same project)
  // ! Important: In project history, we always show operator column because multiple operators
  // ! can work on the same project, and everyone needs to see who performed each action
  const showOperatorColumn = true
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })
  
  const [filters, setFilters] = useState({
    status: undefined as string | undefined,
    operator_id: undefined as string | undefined,
    rejected_reason: undefined as string | undefined,
  })
  
  const [rejectedReasons, setRejectedReasons] = useState<string[]>([])
  const [loadingReasons, setLoadingReasons] = useState(false)
  const [exporting, setExporting] = useState(false)

  // * Cache for source history (for tooltips)
  const [sourceHistoryCache, setSourceHistoryCache] = useState<Record<number, HistoryItem[]>>({})
  const [loadingSourceHistory, setLoadingSourceHistory] = useState<Record<number, boolean>>({})

  // * Fetch source history for tooltip
  const fetchSourceHistory = async (sourceId: number) => {
    // * Check cache first
    if (sourceHistoryCache[sourceId]) return

    // * Check if already loading
    if (loadingSourceHistory[sourceId]) return

    setLoadingSourceHistory(prev => ({ ...prev, [sourceId]: true }))

    try {
      const response = await api.getSourceStatusHistory(sourceId)
      setSourceHistoryCache(prev => ({ ...prev, [sourceId]: response.items || [] }))
    } catch (error) {
      console.error('Failed to fetch source history:', error)
    } finally {
      setLoadingSourceHistory(prev => ({ ...prev, [sourceId]: false }))
    }
  }

  // * Render tooltip content with history
  const renderHistoryTooltip = (sourceId: number) => {
    const historyItems = sourceHistoryCache[sourceId]
    const isLoading = loadingSourceHistory[sourceId]

    if (isLoading) {
      return (
        <div style={{ padding: '8px', textAlign: 'center' }}>
          <Spin size="small" />
          <div style={{ marginTop: 4, fontSize: '12px' }}>Загрузка...</div>
        </div>
      )
    }

    if (!historyItems || historyItems.length === 0) {
      return (
        <div style={{ padding: '8px', fontSize: '12px', color: '#999' }}>
          Нет истории изменений
        </div>
      )
    }

    return (
      <div style={{ maxWidth: 350, maxHeight: 300, overflow: 'auto' }}>
        <div style={{ fontWeight: 600, marginBottom: 8, borderBottom: '1px solid #303030', paddingBottom: 4 }}>
          <HistoryOutlined style={{ marginRight: 6 }} />
          История изменений
        </div>
        {historyItems.map((item, index) => (
          <div
            key={item.id}
            style={{
              padding: '6px 0',
              borderBottom: index < historyItems.length - 1 ? '1px solid #303030' : 'none',
              fontSize: '12px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ color: '#999' }}>
                {dayjs(item.created_at).format('DD.MM.YY HH:mm')}
              </span>
              <span style={{ color: '#8c8c8c' }}>
                {item.operator_full_name || item.operator_username}
              </span>
            </div>
            <div>
              <Tag
                color={item.new_status === 'active' ? 'success' : item.new_status === 'inactive' ? 'error' : 'default'}
                style={{ fontSize: '10px', padding: '0 4px', marginRight: 0 }}
              >
                {statusLabels[item.old_status || 'pending']} → {statusLabels[item.new_status]}
              </Tag>
            </div>
            {item.comment && (
              <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: '11px' }}>
                {item.comment.replace('[ADMIN MANUAL OVERRIDE]', '').trim().substring(0, 50)}
                {item.comment.length > 50 ? '...' : ''}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  const fetchHistory = async () => {
    try {
      setLoading(true)
      
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        project_id: projectId,
        status: filters.status,
        operator_id: filters.operator_id ? parseInt(filters.operator_id) : undefined,
        rejected_reason: filters.rejected_reason,
      }
      
      const response = isManagerOrAdmin 
        ? await api.getAllHistory(params)
        : await api.getMyHistory(params)
      
      setHistory(response.items)
      setPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
      })
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Ошибка загрузки истории')
    } finally {
      setLoading(false)
    }
  }
  
  const fetchStats = async () => {
    try {
      const myStats = await api.getMyStats(projectId)
      setStats(myStats)
      
      if (isManagerOrAdmin) {
        const allStatsResponse = await api.getAllStats(projectId)
        setAllStats(allStatsResponse.operators || [])
      }
    } catch (error: any) {
      console.error('Failed to fetch stats:', error)
    }
  }
  
  // * Fetch rejected reasons
  const fetchRejectedReasons = async () => {
    try {
      setLoadingReasons(true)
      const response = await api.getRejectedReasons({ project_id: projectId })
      setRejectedReasons(response.reasons || [])
    } catch (error: any) {
      console.error('Failed to fetch rejected reasons:', error)
    } finally {
      setLoadingReasons(false)
    }
  }

  // * Export history to .xlsx with current filters applied
  const handleExportXlsx = async () => {
    try {
      setExporting(true)
      const blob = await api.exportHistory({
        project_id: projectId,
        status: filters.status,
        operator_id: filters.operator_id ? parseInt(filters.operator_id) : undefined,
        rejected_reason: filters.rejected_reason,
        tz_offset: -new Date().getTimezoneOffset(),
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `история_действий_${projectId}_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      // * Deferred revoke — Safari may cancel the download on sync revoke
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      message.success('Файл .xlsx сформирован')
    } catch (error: any) {
      // * Error body is a Blob because of responseType: 'blob'
      let detail = 'Ошибка выгрузки .xlsx'
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text()
          const parsed = JSON.parse(text)?.detail
          // * 422 validation errors come as an array of objects
          if (typeof parsed === 'string' && parsed) detail = parsed
        }
      } catch {
        // ignore parse errors, keep default message
      }
      message.error(detail)
    } finally {
      setExporting(false)
    }
  }
  
  useEffect(() => {
    fetchHistory()
    fetchStats()
    fetchRejectedReasons()
  }, [pagination.current, filters.status, filters.operator_id, filters.rejected_reason, projectId])
  
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination({
      ...pagination,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || pagination.pageSize,
    })
  }
  
  const getActionLabel = (oldStatus: string | null, newStatus: string, comment: string | null) => {
    // * Check if this is an admin manual override
    const isAdminOverride = comment?.includes('[ADMIN MANUAL OVERRIDE]')
    
    if (isAdminOverride) {
      // * Show status change for admin overrides
      return { 
        icon: <ToolOutlined />, 
        label: `${statusLabels[oldStatus || 'pending']} → ${statusLabels[newStatus]}`, 
        color: 'orange' 
      }
    }
    
    if (oldStatus === 'pending' && newStatus === 'active') {
      return { icon: <CheckCircleOutlined />, label: 'Одобрено', color: 'success' }
    } else if (oldStatus === 'pending' && newStatus === 'inactive') {
      return { icon: <CloseCircleOutlined />, label: 'Отклонено', color: 'error' }
    } else if ((oldStatus === 'active' || oldStatus === 'inactive') && newStatus === 'pending') {
      return { icon: <UndoOutlined />, label: 'Возврат', color: 'warning' }
    } else {
      return { icon: null, label: `${statusLabels[oldStatus || '']} → ${statusLabels[newStatus]}`, color: 'default' }
    }
  }
  
  // * Admin: Open status edit modal
  const handleAdminOpenModal = (record: HistoryItem) => {
    setAdminModalSource({
      source_id: record.source_id,
      source: record.source,
      current_status: record.new_status,
    })
    adminStatusForm.setFieldsValue({
      status: record.new_status,
      comment: '',
    })
    setAdminModalVisible(true)
  }
  
  // * Admin: Submit status change
  const handleAdminStatusSubmit = async () => {
    if (!adminModalSource) return
    
    try {
      setUpdatingAdminStatus(true)
      const values = await adminStatusForm.validateFields()
      
      await api.adminUpdateSourceStatus(adminModalSource.source_id, {
        status: values.status,
        comment: values.comment,
      })
      
      message.success(`Статус источника ${adminModalSource.source} успешно обновлен`)
      setAdminModalVisible(false)
      setAdminModalSource(null)
      adminStatusForm.resetFields()
      
      // * Refresh history and stats
      await fetchHistory()
      await fetchStats()
    } catch (error: any) {
      console.error('Admin status update error:', error)
      
      if (error.response?.status === 403) {
        message.error('Недостаточно прав для выполнения операции')
      } else {
        message.error(error.response?.data?.detail || 'Ошибка обновления статуса')
      }
    } finally {
      setUpdatingAdminStatus(false)
    }
  }
  
  const columns: ColumnsType<HistoryItem> = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => {
        const local = dayjs.utc(date).local()
        return (
          <Space direction="vertical" size={0}>
            <span>{local.format('DD.MM.YYYY HH:mm')}</span>
            <span style={{ fontSize: '12px', color: '#999' }}>
              {local.fromNow()}
            </span>
          </Space>
        )
      },
    },
    {
      title: 'Источник',
      dataIndex: 'source',
      key: 'source',
      render: (source: string, record: HistoryItem) => {
        // * Build clickable link for domains
        const renderSource = () => {
          if (record.source_type === 'domain') {
            const url = source.startsWith('http') ? source : `https://${source}`
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff' }}
              >
                {source}
              </a>
            )
          }
          return <span>{source}</span>
        }

        return (
          <Space direction="vertical" size={0}>
            {renderSource()}
            <Tag color={record.source_type === 'domain' ? 'blue' : 'green'}>
              {record.source_type === 'domain' ? 'Домен' : 'Телефон'}
            </Tag>
          </Space>
        )
      },
    },
    {
      title: 'Действие',
      key: 'action',
      width: 200,
      render: (_: any, record: HistoryItem) => {
        const action = getActionLabel(record.old_status, record.new_status, record.comment)
        return (
          <Tooltip
            title={renderHistoryTooltip(record.source_id)}
            placement="right"
            onOpenChange={(open) => {
              if (open) {
                fetchSourceHistory(record.source_id)
              }
            }}
          >
            <Tag
              icon={action.icon}
              color={action.color}
              style={{ cursor: 'pointer' }}
            >
              {action.label}
              <HistoryOutlined style={{ marginLeft: 6, fontSize: '10px', opacity: 0.6 }} />
            </Tag>
          </Tooltip>
        )
      },
    },
    ...(showOperatorColumn ? [{
      title: 'Оператор',
      key: 'operator',
      render: (_: any, record: HistoryItem) => (
        <Space direction="vertical" size={0}>
          <span>{record.operator_full_name || record.operator_username}</span>
          <span style={{ fontSize: '12px', color: '#999' }}>
            @{record.operator_username}
          </span>
        </Space>
      ),
    }] : []),
    {
      title: 'Комментарий',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      render: (comment: string | null, record: HistoryItem) => {
        const hasComment = comment && comment.trim()
        const hasRejectedReason = record.rejected_reason && record.rejected_reason.trim()
        const isRejected = record.new_status === 'inactive'
        
        // * If no comment and no rejected_reason
        if (!hasComment && !hasRejectedReason) {
          return <span style={{ color: '#999' }}>—</span>
        }
        
        // * Check if this is an admin manual override
        const isAdminOverride = comment?.includes('[ADMIN MANUAL OVERRIDE]')
        
        // * Build display content
        const content: JSX.Element[] = []
        
        // * Show rejected_reason if status is inactive and rejected_reason exists
        if (isRejected && hasRejectedReason) {
          content.push(
            <Space direction="vertical" size={4} key="rejected_reason" style={{ width: '100%' }}>
              <Tag color="error" style={{ fontSize: '10px', marginBottom: 4 }}>
                Причина отклонения:
              </Tag>
              <span style={{ fontSize: '13px', color: '#262626' }}>{record.rejected_reason}</span>
            </Space>
          )
        }
        
        // * Show comment if exists
        if (hasComment) {
          if (isAdminOverride) {
            // * Extract comment without prefix
            const cleanComment = comment.replace('[ADMIN MANUAL OVERRIDE]', '').trim()
            content.push(
              <Space direction="vertical" size={4} key="admin_comment" style={{ width: '100%', marginTop: isRejected && hasRejectedReason ? 8 : 0 }}>
                <Tag color="orange" style={{ fontSize: '10px', marginBottom: 4 }}>
                  Ручное изменение
                </Tag>
                <span style={{ fontSize: '13px' }}>{cleanComment}</span>
              </Space>
            )
          } else {
            content.push(
              <span key="comment" style={{ fontSize: '13px', marginTop: isRejected && hasRejectedReason ? 8 : 0, display: 'block' }}>
                {comment}
              </span>
            )
          }
        }
        
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            {content}
          </Space>
        )
      },
    },
    ...(isManagerOrAdmin ? [{
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: HistoryItem) => (
        <Button
          size="small"
          type="dashed"
          icon={<ToolOutlined />}
          onClick={() => handleAdminOpenModal(record)}
          style={{
            height: '24px',
            fontSize: '11px',
            padding: '0 8px',
          }}
          title="Изменить статус вручную"
        >
          Статус
        </Button>
      ),
    }] : []),
  ]
  
  return (
    <div>
      {/* * Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {stats && (
          <>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Всего действий"
                  value={stats.total_actions}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Одобрено"
                  value={stats.approved_count}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Отклонено"
                  value={stats.rejected_count}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Отменено"
                  value={stats.reverted_count}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<UndoOutlined />}
                />
              </Card>
            </Col>
          </>
        )}
      </Row>
      
      {/* * All operators stats (for managers) */}
      {isManagerOrAdmin && allStats.length > 0 && (
        <Card title="Статистика по всем операторам" style={{ marginBottom: 24 }}>
          <Table
            dataSource={allStats}
            pagination={false}
            size="small"
            rowKey="operator_id"
            columns={[
              {
                title: 'Оператор',
                key: 'operator',
                render: (_: any, record: OperatorStats) => (
                  <Space direction="vertical" size={0}>
                    <span>{record.operator_full_name || record.operator_username}</span>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      @{record.operator_username}
                    </span>
                  </Space>
                ),
              },
              {
                title: 'Всего действий',
                dataIndex: 'total_actions',
                key: 'total_actions',
              },
              {
                title: 'Одобрено',
                dataIndex: 'approved_count',
                key: 'approved_count',
                render: (count: number) => (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {count}
                  </Tag>
                ),
              },
              {
                title: 'Отклонено',
                dataIndex: 'rejected_count',
                key: 'rejected_count',
                render: (count: number) => (
                  <Tag color="error" icon={<CloseCircleOutlined />}>
                    {count}
                  </Tag>
                ),
              },
              {
                title: 'Отменено',
                dataIndex: 'reverted_count',
                key: 'reverted_count',
                render: (count: number) => (
                  <Tag color="warning" icon={<UndoOutlined />}>
                    {count}
                  </Tag>
                ),
              },
              {
                title: 'Действия',
                key: 'actions',
                width: 120,
                render: (_: any, record: OperatorStats) => (
                  <Button
                    size="small"
                    onClick={() => {
                      setFilters({ ...filters, operator_id: record.operator_id.toString() })
                      setPagination({ ...pagination, current: 1 })
                    }}
                    style={{
                      fontSize: '11px',
                      padding: '0 8px',
                      height: '24px',
                    }}
                  >
                    Показать действия
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}
      
      {/* * Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Select
            placeholder="Фильтр по действию"
            style={{ width: 200 }}
            allowClear
            value={filters.status}
            onChange={(value) => {
              setFilters({ ...filters, status: value })
              setPagination({ ...pagination, current: 1 })
            }}
          >
            <Option value="active">Одобрено</Option>
            <Option value="inactive">Отклонено</Option>
            <Option value="pending">Ожидает</Option>
          </Select>
          
          {isManagerOrAdmin && (
            <Select
              placeholder="Фильтр по оператору"
              style={{ width: 200 }}
              allowClear
              value={filters.operator_id}
              onChange={(value) => {
                setFilters({ ...filters, operator_id: value })
                setPagination({ ...pagination, current: 1 })
              }}
            >
              {allStats.map((op) => (
                <Option key={op.operator_id} value={op.operator_id.toString()}>
                  {op.operator_full_name || op.operator_username}
                </Option>
              ))}
            </Select>
          )}
          
          <Select
            placeholder="Фильтр по причине отказа"
            style={{ width: 250 }}
            allowClear
            value={filters.rejected_reason}
            onChange={(value) => {
              setFilters({ ...filters, rejected_reason: value })
              setPagination({ ...pagination, current: 1 })
            }}
            loading={loadingReasons}
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.label === 'string' 
                ? option.label 
                : typeof option?.children === 'string'
                ? option.children
                : String(option?.children || '')
              return label.toLowerCase().includes(input.toLowerCase())
            }}
          >
            {rejectedReasons.map((reason) => (
              <Option key={reason} value={reason} label={reason}>
                {reason}
              </Option>
            ))}
          </Select>
          
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchHistory()
              fetchStats()
              fetchRejectedReasons()
            }}
          >
            Обновить
          </Button>

          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportXlsx}
            loading={exporting}
            disabled={pagination.total === 0}
          >
            Выгрузить .xlsx
          </Button>
        </Space>
      </Card>
      
      {/* * History table */}
      <Table
        columns={columns}
        dataSource={history}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Всего: ${total}`,
        }}
        onChange={handleTableChange}
      />
      
      {/* * Admin Status Edit Modal */}
      <Modal
        title={
          <Space>
            <ToolOutlined />
            <span>Изменить статус источника (Руководитель)</span>
          </Space>
        }
        open={adminModalVisible}
        onCancel={() => {
          setAdminModalVisible(false)
          setAdminModalSource(null)
          adminStatusForm.resetFields()
        }}
        onOk={handleAdminStatusSubmit}
        confirmLoading={updatingAdminStatus}
        okText="Применить"
        cancelText="Отмена"
        width={600}
      >
        {adminModalSource && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <span style={{ fontWeight: 600 }}>Источник:</span> <Tag>{adminModalSource.source}</Tag>
              <span style={{ marginLeft: 8, color: '#999' }}>
                Текущий статус: <Tag>{statusLabels[adminModalSource.current_status] || adminModalSource.current_status}</Tag>
              </span>
            </div>
            
            <Form
              form={adminStatusForm}
              layout="vertical"
            >
              <Form.Item
                name="status"
                label="Новый статус"
                rules={[{ required: true, message: 'Выберите статус' }]}
              >
                <Select
                  placeholder="Выберите статус"
                  options={[
                    { value: 'pending', label: 'Ожидание' },
                    { value: 'pre_approved', label: 'Предварительно одобрен' },
                    { value: 'active', label: 'Одобрен' },
                    { value: 'inactive', label: 'Неактивен' },
                    { value: 'deleted', label: 'Удален' },
                  ]}
                />
              </Form.Item>
              
              <Form.Item
                name="comment"
                label="Комментарий"
                rules={[{ required: true, message: 'Укажите причину изменения статуса' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Например: Исправление ошибки, источник kreditnykomissar.ru был неправильно обработан"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Form>
            
            <div style={{ 
              padding: '8px 12px', 
              background: '#fff7e6', 
              border: '1px solid #ffd591',
              borderRadius: '4px'
            }}>
              <span style={{ fontSize: '12px', color: '#faad14' }}>
                ⚠️ Это действие будет записано в историю статусов с отметкой "[ADMIN MANUAL OVERRIDE]"
              </span>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  )
}

export default ActionHistoryTab

